import type {
  DocumentExportFormat,
  DocumentExportSkipReason,
} from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Dialog } from '@utima/ui';
import { Download } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  exportDocuments,
  type ExportDocument,
  type DocumentExportSummary,
} from '../lib/exportDocuments';

export function DocumentExportModal({
  documents,
  onClose,
}: {
  documents: ExportDocument[];
  onClose: () => void;
}) {
  const { t } = useLingui();
  const [format, setFormat] = useState<DocumentExportFormat>('markdown');
  const [isExporting, setIsExporting] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [summary, setSummary] = useState<DocumentExportSummary | null>(null);
  const [error, setError] = useState(false);
  const running = useRef(false);
  const reasons: Record<DocumentExportSkipReason, string> = {
    unavailable: t`Source or document is no longer available, or could not be downloaded.`,
    empty: t`No content available`,
    too_large: t`File or export size limit exceeded.`,
  };

  const handleDownload = async () => {
    if (running.current) return;

    running.current = true;
    setIsExporting(true);
    setSummary(null);
    setError(false);

    try {
      setSummary(
        await exportDocuments({ documents, format, onProgress: setCompleted }),
      );
    } catch {
      setError(true);
    } finally {
      running.current = false;
      setIsExporting(false);
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !running.current) onClose();
      }}
    >
      <Dialog.Content className='max-h-[90vh] max-w-lg overflow-y-auto'>
        <Dialog.Header>
          <Dialog.Title>
            <Trans>Download documents</Trans>
          </Dialog.Title>
          <Dialog.Description className='break-words'>
            {documents.length === 1
              ? documents[0].name || t`Choose a download format.`
              : t`${documents.length} selected documents. Files will be combined into a ZIP.`}
          </Dialog.Description>
        </Dialog.Header>

        <fieldset disabled={isExporting} className='space-y-3'>
          <legend className='sr-only'>
            <Trans>Download format</Trans>
          </legend>
          <label className='flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 has-[:checked]:border-primary has-[:checked]:bg-primary/5'>
            <input
              type='radio'
              name='document-export-format'
              value='markdown'
              checked={format === 'markdown'}
              onChange={() => {
                setFormat('markdown');
                setSummary(null);
              }}
              className='mt-1 accent-primary'
            />
            <span>
              <span className='block font-medium'>
                <Trans>Markdown — indexed content</Trans>
              </span>
              <span className='mt-1 block text-sm text-muted-fg'>
                <Trans>
                  The processed text currently stored in Kanbu. Original page
                  layout and file formatting are not preserved.
                </Trans>
              </span>
            </span>
          </label>
          <label className='flex cursor-pointer items-start gap-3 rounded-lg border border-border p-4 has-[:checked]:border-primary has-[:checked]:bg-primary/5'>
            <input
              type='radio'
              name='document-export-format'
              value='source'
              checked={format === 'source'}
              onChange={() => {
                setFormat('source');
                setSummary(null);
              }}
              className='mt-1 accent-primary'
            />
            <span>
              <span className='block font-medium'>
                <Trans>Source file</Trans>
              </span>
              <span className='mt-1 block text-sm text-muted-fg'>
                <Trans>
                  Download the saved upload or synced file, or fetch the current
                  online source. Online content may have changed since indexing.
                  Unavailable sources will be skipped.
                </Trans>
              </span>
            </span>
          </label>
        </fieldset>

        {format === 'source' && (
          <p className='text-sm text-muted-fg'>
            <Trans>
              Web pages download as HTML without bundled images or styles.
              Synced Google files use their saved format: Docs as Markdown,
              Sheets as CSV, and Slides as PDF.
            </Trans>
          </p>
        )}
        <p className='text-xs text-muted-fg'>
          <Trans>
            Up to 50 MB per file and 1 GB per export. Files that exceed these
            limits are skipped.
          </Trans>
        </p>

        {isExporting && (
          <p role='status' className='text-sm'>
            {completed === documents.length
              ? t`Preparing download…`
              : t`Preparing ${completed} of ${documents.length} documents…`}
          </p>
        )}
        {error && (
          <p role='alert' className='text-sm text-danger'>
            <Trans>Could not create the download. Please try again.</Trans>
          </p>
        )}
        {summary && (
          <div
            className='space-y-2'
            role={summary.downloaded ? 'status' : 'alert'}
          >
            <p
              className={summary.downloaded ? 'text-sm' : 'text-sm text-danger'}
            >
              {summary.downloaded
                ? t`Downloaded ${summary.downloaded} of ${summary.total} documents.`
                : t`No documents could be downloaded.`}
            </p>
            {summary.skipped.length > 0 && (
              <details className='text-sm' open>
                <summary>
                  <Trans>Skipped documents ({summary.skipped.length})</Trans>
                </summary>
                <ul className='mt-2 max-h-36 space-y-2 overflow-y-auto'>
                  {summary.skipped.map(document => (
                    <li key={document.id}>
                      <span className='break-all font-medium'>
                        {document.name || document.id}
                      </span>
                      <span className='block text-muted-fg'>
                        {reasons[document.reason]}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <Dialog.Footer>
          <Button variant='ghost' onClick={onClose} disabled={isExporting}>
            <Trans>Close</Trans>
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isExporting}
            loading={isExporting}
            icon={<Download className='size-4' />}
          >
            <Trans>Download</Trans>
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}
